import { notFound, redirect } from "@tanstack/react-router";
import { createServerOnlyFn } from "@tanstack/react-start";
import {
    readShortLinkLocationAttribution,
    SHORT_LINK_PRIVATE_NO_STORE_HEADERS,
    type ShortLinkRequestAttribution,
} from "./short-link-attribution";
import { resolveShortLink } from "./resolve-short-link";

const KITS_SEARCH_ID_RE = /^[A-Za-z]{1,8}$/;

type ShortLinkRedirectRequest = {
    slug: string;
    caseSensitive: boolean;
    attribution: ShortLinkRequestAttribution;
};

export function shortLinkRedirectOptions(destinationHref: string) {
    return {
        href: destinationHref,
        statusCode: 302,
        headers: SHORT_LINK_PRIVATE_NO_STORE_HEADERS,
    } as const;
}

export function ShortLinkRedirect() {
    return (
        <div className="flex min-h-dvh items-center justify-center">
            <p className="text-sm text-neutral-500">Redirecting…</p>
        </div>
    );
}

const readShortLinkServerRequestAttribution = createServerOnlyFn(
    async (): Promise<ShortLinkRequestAttribution> => {
        const { getRequest, getRequestUrl } =
            await import("@tanstack/react-start/server");
        return readShortLinkLocationAttribution(
            getRequestUrl().href,
            getRequest().headers.get("referer") ?? "",
        );
    },
);

async function getShortLinkRequestAttribution(
    requestedHref?: string,
): Promise<ShortLinkRequestAttribution> {
    if (typeof window !== "undefined") {
        return readShortLinkLocationAttribution(
            new URL(
                requestedHref ?? window.location.href,
                window.location.origin,
            ).href,
            document.referrer,
        );
    }

    return await readShortLinkServerRequestAttribution();
}

async function redirectToShortLink({
    slug,
    caseSensitive,
    attribution,
}: ShortLinkRedirectRequest) {
    const destinationUrl = await resolveShortLink({
        data: { slug, caseSensitive, ...attribution },
    });

    if (!destinationUrl) throw notFound();

    if (typeof window !== "undefined") {
        window.location.href = destinationUrl.destinationHref;
        return;
    }

    throw redirect(shortLinkRedirectOptions(destinationUrl.destinationHref));
}

async function loadShortLink({
    context,
    cause,
}: {
    context: { shortLinkRequest?: ShortLinkRedirectRequest };
    cause: string;
}) {
    if (cause === "preload" || !context.shortLinkRequest) return;
    return await redirectToShortLink(context.shortLinkRequest);
}

export const shortLinkRoute = {
    beforeLoad: async ({
        params,
        location,
    }: {
        params: { _splat?: string };
        location: { href: string };
    }) => {
        const slug = (params._splat ?? "").split("/")[0];
        if (!slug) throw notFound();
        return {
            shortLinkRequest: {
                slug,
                caseSensitive: false,
                attribution: await getShortLinkRequestAttribution(
                    location.href,
                ),
            },
        };
    },
    loader: loadShortLink,
};

export const kitsSearchShortLinkRoute = {
    beforeLoad: async ({
        params,
        location,
    }: {
        params: { id: string };
        location: { href: string };
    }) => {
        if (!KITS_SEARCH_ID_RE.test(params.id)) throw notFound();
        return {
            shortLinkRequest: {
                slug: `kits-search/${params.id}`,
                caseSensitive: true,
                attribution: await getShortLinkRequestAttribution(
                    location.href,
                ),
            },
        };
    },
    loader: loadShortLink,
};
